import React from 'react';

import classNames from 'classnames';
import pluralize from 'pluralize';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import CircleQuestion from 'components/application/CircleQuestion';
import CollectionSelect from 'components/application/CollectionSelect';
import CurrencyField from 'components/application/CurrencyField';
import RadioField from 'components/application/RadioField';
import TextArea from 'components/application/TextArea';
import TextField from 'components/application/TextField';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import NonCustomELearningCourseCard from 'components/courses/NonCustomELearningCourseCard';
import SearchELearningCoursesField from 'components/courses/SearchELearningCoursesField';
import CourseSidePanelAutoEnrolmentForm from 'components/courses/sidepanel/CourseSidePanelAutoEnrolmentForm';

import { scrollToBottom } from 'components/helpers/scrolling';

export default function CourseSidePanelProviderForm({
  bodyRef,
  course,
  onELearningProviderSelectedChange,
  onInputChange,
  onOptionChange,
  removeErrorStyling,
  requestError,
}) {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'courses.sidepanel.CourseSidePanelProviderForm',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const learningMethodOptions = [
    {
      label: i18nTCommon('courses.learningMethods.atpApprovedTrainingProvider'),
      value: 'approved_training_provider',
    },
    {
      label: i18nTCommon('courses.learningMethods.internal'),
      value: 'internal',
    },
    { label: i18nTCommon('courses.learningMethods.online'), value: 'online' },
  ];

  const timeIntervalOptions = ['minute', 'hour', 'day', 'week'].map(
    (option) => ({
      label: i18nTCommon(`shared.intervals.${pluralize(option)}`),
      value: option,
    }),
  );

  return (
    <div className='form-container'>
      {currentActor.isAllowedFeature('e_learning') && (
        <>
          <label
            className='field__label tw-font-medium'
            htmlFor='isELearningProviderSelected'
          >
            {i18nTCommon('courses.courseDetails')}
            <Tooltip
              placement='top'
              tooltip={
                <div className='tw-text-center'>
                  <Trans
                    components={{
                      a: (
                        /* eslint-disable jsx-a11y/anchor-has-content */
                        <a
                          className='app-link tw-align-baseline tw-font-medium tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
                          href='https://intercom.help/hands-hq/en/articles/5870952-get-started-with-elearning-assign-and-book-online-training'
                          target='blank'
                        />
                        /* eslint-enable jsx-a11y/anchor-has-content */
                      ),
                    }}
                    i18nKey='courses.sidepanel.CourseSidePanelProviderForm.isELearningProviderSelected.tooltip'
                    ns='components'
                  />
                </div>
              }
              trigger='hover'
            >
              <CircleQuestion />
            </Tooltip>
          </label>
          <RadioField
            checked={course.isELearningProviderSelected}
            label={i18nT(
              'isELearningProviderSelected.radioFields.iHasco.label',
            )}
            name='isELearningProviderSelected'
            onChange={() => {
              onELearningProviderSelectedChange({ selected: true });
            }}
            value={true}
          />
          {course.isELearningProviderSelected && (
            <div className='tw-ml-7'>
              {course.eLearningCourseId ?
                <>
                  <NonCustomELearningCourseCard
                    disabledTooltip={i18nT('eLearningCourseId.disabledTooltip')}
                    eLearningCourseId={course.eLearningCourseId}
                    isDisabled={course.activeRegistrationCount > 0}
                    onClose={() =>
                      onOptionChange(
                        { value: null },
                        { name: 'eLearningCourseId' },
                      )
                    }
                    onError={breadBoard.addInedibleToast}
                  />
                  {currentActor.isAllowedFeature([
                    'e_learning_auto_enrol',
                    'e_learning',
                    'training_register',
                  ]) && (
                    <CourseSidePanelAutoEnrolmentForm
                      autoEnrolCourseRequirementScope={
                        course.autoEnrolCourseRequirementScope
                      }
                      autoEnrolEnrolleeScope={course.autoEnrolEnrolleeScope}
                      hasAutoEnrolBeenActivelySelected={
                        course.hasAutoEnrolBeenActivelySelected
                      }
                      isExpiringCourse={course.expires}
                      onInputChange={onInputChange}
                      onOptionChange={onOptionChange}
                    />
                  )}
                </>
              : <SearchELearningCoursesField
                  fieldError={requestError.validationErrors.eLearningCourseId}
                  name='eLearningCourseId'
                  onError={breadBoard.addInedibleToast}
                  onSelect={onOptionChange}
                  removeErrorStyling={removeErrorStyling}
                />
              }
            </div>
          )}
          <RadioField
            checked={!course.isELearningProviderSelected}
            disabled={course.activeRegistrationCount > 0}
            label={i18nT('isELearningProviderSelected.radioFields.other.label')}
            name='isELearningProviderSelected'
            onChange={() => {
              onELearningProviderSelectedChange({ selected: false });
            }}
            tooltip={i18nT(
              'isELearningProviderSelected.radioFields.other.tooltip',
            )}
            value={false}
          />
        </>
      )}
      {!course.isELearningProviderSelected && (
        <div
          className={classNames(
            currentActor.isAllowedFeature('e_learning') && 'tw-ml-7',
          )}
        >
          <CollectionSelect
            label={i18nTCommon('shared.method')}
            name='learningMethod'
            onChange={onOptionChange}
            options={learningMethodOptions}
            value={
              learningMethodOptions.find(
                (option) => course.learningMethod === option.value,
              ) || null
            }
          />
          <TextField
            fieldError={requestError.validationErrors.provider}
            isRequired={false}
            label={i18nTCommon('courses.provider')}
            name='provider'
            onChange={onInputChange}
            removeErrorStyling={removeErrorStyling}
            value={course.provider}
          />
          <CurrencyField
            currencyCode={currentActor.division.attributes.currencyCode}
            fieldError={requestError.validationErrors.costEstimate}
            isRequired={false}
            label={i18nTCommon('courses.costEstimate')}
            name='costEstimate'
            onChange={onInputChange}
            removeErrorStyling={removeErrorStyling}
            value={course.costEstimate}
          />
          <div className='side-panel__inline-container'>
            <div className='field--inline tw-mr-1.5 tw-flex-1'>
              <TextField
                fieldError={requestError.validationErrors.timeAmount}
                isRequired={false}
                label={i18nTCommon('courses.timeEstimate')}
                name='timeAmount'
                onChange={onInputChange}
                removeErrorStyling={removeErrorStyling}
                value={course.timeAmount}
              />
            </div>
            <div className='field--inline tw-ml-1.5 tw-flex-1'>
              <CollectionSelect
                externalProps={{ onMenuOpen: () => scrollToBottom(bodyRef) }}
                label=''
                name='timeInterval'
                onChange={onOptionChange}
                options={timeIntervalOptions}
                value={timeIntervalOptions.find(
                  (option) => course.timeInterval === option.value,
                )}
              />
            </div>
          </div>
          <div className='tw-relative'>
            <TextArea
              label={i18nTCommon('courses.defaultBookingNotes')}
              name='defaultBookingNote'
              onChange={onInputChange}
              placeholderText={i18nT('defaultBookingNote.placeholder')}
              tooltip={
                <div className='ta-center tw-text-s tw-font-normal tw-tracking-wide'>
                  {i18nT('defaultBookingNote.tooltip')}
                </div>
              }
              value={course.defaultBookingNote}
            />
          </div>
        </div>
      )}
    </div>
  );
}

CourseSidePanelProviderForm.propTypes = {
  course: PropTypes.object.isRequired,
  requestError: PropTypes.object.isRequired,
  removeErrorStyling: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onOptionChange: PropTypes.func.isRequired,
  onELearningProviderSelectedChange: PropTypes.func.isRequired,
  bodyRef: PropTypes.object.isRequired,
};
